/**
 * Country, dial-code and flag data for PhoneInput and CountryPicker.
 *
 * Rendered names follow the visitor's language; the submitted English and
 * Spanish names are fixed, since downstream consumers (e.g. Salesforce) key on them.
 */
import countries from 'i18n-iso-countries';
import deLocale from 'i18n-iso-countries/langs/de.json';
import enLocale from 'i18n-iso-countries/langs/en.json';
import esLocale from 'i18n-iso-countries/langs/es.json';
import frLocale from 'i18n-iso-countries/langs/fr.json';
import { BASE_LANGUAGE, type WidgetLanguage } from './i18n';

countries.registerLocale(deLocale);
countries.registerLocale(enLocale);
countries.registerLocale(esLocale);
countries.registerLocale(frLocale);

/** Lowercase and strip diacritics, so "espana" matches "España". */
export function normalizeForSearch(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/** Convert ISO alpha-2 code to flag emoji using regional indicator symbols */
export function getFlagEmoji(countryCode: string): string {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 0x1f1e6 + char.charCodeAt(0) - 65);
  return String.fromCodePoint(...codePoints);
}

/** Dial codes by ISO alpha-2 country code */
export const DIAL_CODES: Record<string, string> = {
  AF: '+93', AL: '+355', DZ: '+213', AS: '+1-684', AD: '+376', AO: '+244',
  AG: '+1-268', AR: '+54', AM: '+374', AU: '+61', AT: '+43', AZ: '+994',
  BS: '+1-242', BH: '+973', BD: '+880', BB: '+1-246', BY: '+375', BE: '+32',
  BZ: '+501', BJ: '+229', BT: '+975', BO: '+591', BA: '+387', BW: '+267',
  BR: '+55', BN: '+673', BG: '+359', BF: '+226', BI: '+257', KH: '+855',
  CM: '+237', CA: '+1', CV: '+238', CF: '+236', TD: '+235', CL: '+56',
  CN: '+86', CO: '+57', KM: '+269', CG: '+242', CD: '+243', CR: '+506',
  CI: '+225', HR: '+385', CU: '+53', CY: '+357', CZ: '+420', DK: '+45',
  DJ: '+253', DM: '+1-767', DO: '+1-809', EC: '+593', EG: '+20', SV: '+503',
  GQ: '+240', ER: '+291', EE: '+372', SZ: '+268', ET: '+251', FJ: '+679',
  FI: '+358', FR: '+33', GA: '+241', GM: '+220', GE: '+995', DE: '+49',
  GH: '+233', GR: '+30', GD: '+1-473', GT: '+502', GN: '+224', GW: '+245',
  GY: '+592', HT: '+509', HN: '+504', HU: '+36', IS: '+354', IN: '+91',
  ID: '+62', IR: '+98', IQ: '+964', IE: '+353', IL: '+972', IT: '+39',
  JM: '+1-876', JP: '+81', JO: '+962', KZ: '+7', KE: '+254', KI: '+686',
  KP: '+850', KR: '+82', KW: '+965', KG: '+996', LA: '+856', LV: '+371',
  LB: '+961', LS: '+266', LR: '+231', LY: '+218', LI: '+423', LT: '+370',
  LU: '+352', MG: '+261', MW: '+265', MY: '+60', MV: '+960', ML: '+223',
  MT: '+356', MH: '+692', MR: '+222', MU: '+230', MX: '+52', FM: '+691',
  MD: '+373', MC: '+377', MN: '+976', ME: '+382', MA: '+212', MZ: '+258',
  MM: '+95', NA: '+264', NR: '+674', NP: '+977', NL: '+31', NZ: '+64',
  NI: '+505', NE: '+227', NG: '+234', MK: '+389', NO: '+47', OM: '+968',
  PK: '+92', PW: '+680', PA: '+507', PG: '+675', PY: '+595', PE: '+51',
  PH: '+63', PL: '+48', PT: '+351', QA: '+974', RO: '+40', RU: '+7',
  RW: '+250', KN: '+1-869', LC: '+1-758', VC: '+1-784', WS: '+685',
  SM: '+378', ST: '+239', SA: '+966', SN: '+221', RS: '+381', SC: '+248',
  SL: '+232', SG: '+65', SK: '+421', SI: '+386', SB: '+677', SO: '+252',
  ZA: '+27', SS: '+211', ES: '+34', LK: '+94', SD: '+249', SR: '+597',
  SE: '+46', CH: '+41', SY: '+963', TW: '+886', TJ: '+992', TZ: '+255',
  TH: '+66', TL: '+670', TG: '+228', TO: '+676', TT: '+1-868', TN: '+216',
  TR: '+90', TM: '+993', TV: '+688', UG: '+256', UA: '+380', AE: '+971',
  GB: '+44', US: '+1', UY: '+598', UZ: '+998', VU: '+678', VE: '+58',
  VN: '+84', YE: '+967', ZM: '+260', ZW: '+263', HK: '+852', MO: '+853',
  PS: '+970', PR: '+1-787', GU: '+1-671', VI: '+1-340', CW: '+599',
  SX: '+1-721', BM: '+1-441', KY: '+1-345', TC: '+1-649', VG: '+1-284',
};

export interface CountryOption {
  code: string;
  name: string;
  flag: string;
}

export interface CountryDialOption extends CountryOption {
  dial: string;
}

const COUNTRY_LISTS = new Map<WidgetLanguage, CountryOption[]>();
const COUNTRY_DIAL_LISTS = new Map<WidgetLanguage, CountryDialOption[]>();

/** All ISO countries named in `lang`, with flags, sorted and cached per language. */
export function getCountryList(lang: WidgetLanguage): CountryOption[] {
  const cached = COUNTRY_LISTS.get(lang);
  if (cached) return cached;
  const list = Object.entries(countries.getNames(lang, { select: 'official' }))
    .map(([code, name]) => ({ code, name, flag: getFlagEmoji(code) }))
    .sort((a, b) => a.name.localeCompare(b.name, lang));
  COUNTRY_LISTS.set(lang, list);
  return list;
}

/** A country's name in `lang` for an ISO alpha-2 code (falls back to the code). */
export function getCountryName(code: string, lang: WidgetLanguage): string {
  return getCountryList(lang).find((c) => c.code === code)?.name || code;
}

/** Countries that have a dial code, named in `lang`, with their prefix. */
export function getCountryDialList(lang: WidgetLanguage): CountryDialOption[] {
  const cached = COUNTRY_DIAL_LISTS.get(lang);
  if (cached) return cached;
  const list = getCountryList(lang)
    .filter((c) => DIAL_CODES[c.code])
    .map((c) => ({ ...c, dial: DIAL_CODES[c.code] }));
  COUNTRY_DIAL_LISTS.set(lang, list);
  return list;
}

/** All ISO countries named in the base language. */
export const COUNTRY_LIST: CountryOption[] = getCountryList(BASE_LANGUAGE);

/** Dial-code countries named in the base language. */
export const COUNTRY_DIAL_LIST: CountryDialOption[] = getCountryDialList(BASE_LANGUAGE);

/** English short country names by ISO alpha-2 code, as downstream CRMs expect. */
const EN_NAMES = countries.getNames('en');

/** English country name for an ISO alpha-2 code (falls back to the code). */
export function getEnglishCountryName(code: string): string {
  return EN_NAMES[code] || code;
}

/**
 * Spanish country name for an ISO alpha-2 code (falls back to the code).
 * Deliberately fixed to Spanish, whatever language the picker renders in: it is
 * the `nombre` of the submitted payload, which must not move with the visitor.
 */
export function getSpanishCountryName(code: string): string {
  return getCountryName(code, BASE_LANGUAGE);
}

/** A country field's value as the form drawer submits it. */
export interface SubmittedCountry {
  code: string;
  name: string;
  nombre: string;
}

/**
 * A picked country in all three representations, so a consumer (a CRM, a
 * spreadsheet) can key on the one it needs.
 */
export function expandCountry(code: string): SubmittedCountry {
  return { code, name: getEnglishCountryName(code), nombre: getSpanishCountryName(code) };
}

function isSubmittedCountry(value: unknown): value is SubmittedCountry {
  if (typeof value !== 'object' || value === null) return false;
  const keys = Object.keys(value).sort().join(',');
  return keys === 'code,name,nombre' && typeof (value as SubmittedCountry).code === 'string';
}

/**
 * Submitted form values with every expanded country back to its ISO code.
 *
 * What a Brain client sends: a Process declares a country as a string, so the
 * object the drawer submits would be refused as the wrong type.
 */
export function withCountryCodes(values: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, isSubmittedCountry(value) ? value.code : value]),
  );
}
